import { useEffect, useState } from "react";
import { RefreshCw, ImageOff, Clock } from "lucide-react";
import { api, getUser } from "../lib/api";

// In-memory cache so switching back to a word doesn't refetch
const _cache = new Map();

export function clearWordImageCache(word) {
  if (word) _cache.delete(String(word).toLowerCase());
  else _cache.clear();
}

export default function WordImage({ word, visible, size = "md", thumb = false }) {
  const [state, setState] = useState({ status: "idle", src: null });
  const [regenning, setRegenning] = useState(false);
  const isAdmin = getUser()?.role === "admin";

  const load = async (signal) => {
    const key = String(word || "").toLowerCase();
    if (!key) return;
    // Thumb requests are cached separately so a card view doesn't poison
    // the larger Practice-mode preview cache.
    const cacheKey = thumb ? `t:${key}` : key;
    if (_cache.has(cacheKey)) {
      setState({ status: "ok", src: _cache.get(cacheKey) });
      return;
    }
    setState({ status: "loading", src: null });
    try {
      const resp = await api.get(`/words/image`, {
        params: thumb ? { word: key, thumb: 1 } : { word: key },
      });
      // 202 means the image hasn't been generated yet — admin approval is
      // pending. We don't cache this; the next call will retry.
      if (resp.status === 202 || resp.data?.status === "pending") {
        if (!signal?.aborted) setState({ status: "pending", src: null });
        return;
      }
      const src = `data:${resp.data.mime || "image/png"};base64,${resp.data.image_base64}`;
      _cache.set(cacheKey, src);
      if (!signal?.aborted) setState({ status: "ok", src });
    } catch (err) {
      const status = err?.response?.status;
      if (!signal?.aborted) setState({ status: status === 402 ? "budget" : "error", src: null });
    }
  };

  useEffect(() => {
    if (!visible || !word) { setState({ status: "idle", src: null }); return; }
    const ac = { aborted: false };
    load(ac);
    return () => { ac.aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, visible]);

  const onRegenerate = async (e) => {
    e.stopPropagation();
    if (regenning || !word) return;
    setRegenning(true);
    const key = String(word).toLowerCase();
    _cache.delete(key);
    setState({ status: "loading", src: null });
    try {
      const { data } = await api.post(`/words/image/regenerate`, { word: key });
      const src = `data:${data.mime || "image/png"};base64,${data.image_base64}`;
      _cache.set(key, src);
      setState({ status: "ok", src });
    } catch (err) {
      const status = err?.response?.status;
      setState({ status: status === 402 ? "budget" : "error", src: null });
    } finally {
      setRegenning(false);
    }
  };

  if (!visible) return null;

  const sizeCls = size === "lg"
    ? "w-56 h-56 sm:w-64 sm:h-64"
    : size === "sm" ? "w-28 h-28" : "w-40 h-40 sm:w-48 sm:h-48";

  return (
    <div className="relative inline-block" data-testid="word-image-container">
      <div
        className={`${sizeCls} mx-auto rounded-3xl border-4 border-blue-100 bg-white overflow-hidden flex items-center justify-center shadow-md`}
      >
        {state.status === "loading" && (
          <div className="w-full h-full bg-gradient-to-br from-blue-50 via-amber-50 to-rose-50 animate-pulse flex items-center justify-center text-slate-400 text-sm font-bold">
            {regenning ? "Redrawing…" : "Drawing…"}
          </div>
        )}
        {state.status === "ok" && (
          <img src={state.src} alt={word} className="w-full h-full object-cover" data-testid="word-image" />
        )}
        {state.status === "pending" && (
          <div
            className="w-full h-full bg-gradient-to-br from-amber-50 via-blue-50 to-amber-50 flex flex-col items-center justify-center text-center px-3 gap-1"
            data-testid="word-image-pending"
          >
            <div className="w-10 h-10 rounded-full bg-[#FBBF24] flex items-center justify-center shadow-[0_3px_0_#B45309]">
              <Clock className="w-5 h-5 text-[#1E1B4B]" strokeWidth={3} />
            </div>
            <div className="text-xs font-bold text-[#1E1B4B] leading-tight">Image Pending</div>
            <div className="text-[10px] text-slate-500 font-semibold leading-tight">
              Waiting for admin approval
            </div>
          </div>
        )}
        {state.status === "error" && (
          <div className="w-full h-full bg-blue-50 flex flex-col items-center justify-center text-slate-400 gap-1" data-testid="word-image-missing">
            <ImageOff className="w-8 h-8" strokeWidth={2.5} />
            <div className="text-xs font-bold">No picture yet</div>
          </div>
        )}
        {state.status === "budget" && (
          <div className="text-xs text-amber-700 font-bold text-center px-3 leading-tight">
            Pictures need credits.<br />Top up the Universal Key.
          </div>
        )}
      </div>
      {word && isAdmin && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenning || state.status === "loading"}
          title="Get a new picture for this word"
          data-testid="regenerate-image-btn"
          className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-white text-[#4D96FF] border-2 border-[#4D96FF] shadow-[0_3px_0_#1D4ED8] active:translate-y-[2px] active:shadow-[0_1px_0_#1D4ED8] flex items-center justify-center hover:bg-blue-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${regenning ? "animate-spin" : ""}`} strokeWidth={3} />
        </button>
      )}
    </div>
  );
}
